import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "../lib/api";

const DEFAULTS = {
    brand: {
        logo: "",
        texture_gold: "",
    },
    hero: {
        eyebrow: "A Delhi NCR Motorcycle Community",
        title_line1: "Meraki",
        title_line2: "Riders",
        title_line3: "Group",
        paragraph: "Founded in 2021. What started as 20 Jawa riders on a single ride to Bheemtal — bound by one shared passion — has grown into a 300+ strong community. United by Meraki: the Greek word for pouring your heart, soul, and creativity into every mile.",
        cta_primary: "Ride With Us →",
        cta_secondary: "Discover the Group",
        background_image: "",
        side_rail: "Heart · Soul · Throttle",
    },
    about: {
        eyebrow: "The Origin",
        heading_line1: "A word from",
        heading_emph: "Greek",
        heading_line2: "A spirit on the road.",
        meraki_definition: "Meraki (μεράκι) — to do something with soul, creativity, and love; to leave a piece of yourself in your work.",
        body: "Founded in 2021, Meraki Riders Group is a thriving motorcycle community based in Delhi NCR.",
        instagram_label: "@merakiriders",
        facebook_label: "Meraki Riders Group",
        image_1: "",
        image_2: "",
        image_3: "",
    },
    rule: { phrase: "Stay Active · Quarterly Show Up · Ride Strong · ", subtitle: "The One Rule · No exceptions · Miss it, you may be removed" },
    how: {
        eyebrow: "How We Work",
        heading_line1: "Two Bodies.",
        heading_line2: "One Spirit.",
        intro: "As we crossed 300+ riders, we evolved our leadership: Governor Bodies hold the vision; Meraki Ambassadors drive the rides.",
        governor_title: "Governor Bodies",
        governor_subtitle: "/ MRG Cofounders",
        governor_label: "The Governors",
        governor_paragraph: "",
        ambassador_title: "Meraki",
        ambassador_title_2: "Ambassadors",
        ambassador_label: "The Driving Force",
        ambassador_paragraph: "",
        riders_count: "300+",
        riders_label: "Active Riders",
        open_positions: "05",
        open_positions_label: "Open Ambassador Roles",
        open_positions_note: "Apply Now →",
        open_positions_url: "",
        open_positions_enabled: "true",
    },
    leaders: { eyebrow: "The People", heading_line1: "The Faces", heading_line2: "Behind The Throttle", cofounders_label: "Co-Founders", ambassadors_label: "Meraki Ambassadors" },
    merch: {
        eyebrow: "The Drop",
        heading_line1: "Meraki",
        heading_line2: "Merchandise",
        paragraph: "Limited gear, built for the road. Wear your Meraki, ride your Meraki.",
        empty_state: "New drops coming soon. Watch this space.",
    },
    gallery: {
        eyebrow: "The Rides",
        heading_line1: "Meraki",
        heading_line2: "Gallery",
        paragraph: "Every ride tells a story — miles ridden, roads explored, memories made. Dive into the moments that define the Meraki spirit.",
        empty_state: "Ride galleries coming soon. Watch this space.",
    },
    dashboard: {
        eyebrow: "The Numbers",
        heading_line1: "Riders, Routes,",
        heading_line2: "Realtime.",
        paragraph: "",
        looker_url: "https://datastudio.google.com/reporting/15cf17ce-443c-4d65-8718-15bcc61ef107/page/MiNQF",
        looker_embed_url: "https://datastudio.google.com/embed/reporting/15cf17ce-443c-4d65-8718-15bcc61ef107/page/MiNQF",
        cta_label: "Open in new tab",
    },
    join: {
        heading_line1: "Pick Your",
        heading_line2: "Entry Point",
        paragraph: "Two ways to start your journey with the Meraki community. Choose what fits you.",
        option1_label: "merakiriders.in",
        option1_url: "https://merakiriders.in",
        option1_desc: "Explore the official webapp and request to join the community.",
        option2_label: "@merakiriders",
        option2_url: "https://instagram.com/merakiriders",
        option2_desc: "DM us on Instagram and connect with the riders directly.",
    },
    footer: { tagline: "", copyright: "© 2021 Meraki Riders Group", rightside: "Ride With Heart", contact_email: "merakiridersgroup@gmail.com", contact_label: "Collaborate · Sponsor · Say Hi", whatsapp_number: "", whatsapp_message: "Hi Meraki Riders! I'd love to connect / collaborate.", whatsapp_label: "Chat on WhatsApp" },
};

function merge(remote) {
    const out = {};
    for (const key of Object.keys(DEFAULTS)) {
        out[key] = { ...DEFAULTS[key], ...(remote?.[key] || {}) };
    }
    return out;
}

const CACHE_KEY = "meraki_content_cache_v1";

function readCache() {
    try {
        const raw = typeof window !== "undefined" ? window.localStorage.getItem(CACHE_KEY) : null;
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") return merge(parsed);
        return null;
    } catch (err) {
        console.warn("ContentContext: failed to read cache", err);
        return null;
    }
}

function writeCache(content) {
    try {
        if (typeof window === "undefined") return;
        window.localStorage.setItem(CACHE_KEY, JSON.stringify(content));
    } catch (err) {
        // Quota exceeded or privacy mode — non-fatal; cache is purely a perf optimisation.
        console.warn("ContentContext: failed to write cache", err);
    }
}

const ContentContext = createContext({ content: DEFAULTS, loading: true, reload: async () => {} });

export function ContentProvider({ children }) {
    // Hydrate synchronously from cache so the first paint already has the right images.
    // Cached payload contains only PUBLIC landing-page content (logos, marketing copy, photo URLs) —
    // no auth tokens or sensitive data (auth uses httpOnly cookies, not localStorage).
    const cached = readCache();
    const [content, setContent] = useState(cached || DEFAULTS);
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get("/content");
            const merged = merge(data);
            setContent(merged);
            writeCache(merged);
        } catch (err) {
            // Keep cached/default content if API fails
            console.warn("ContentContext: /content fetch failed; using cache/defaults", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const value = useMemo(() => ({ content, loading, reload }), [content, loading, reload]);
    return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export const useContent = () => useContext(ContentContext);
export { DEFAULTS as CONTENT_DEFAULTS };
